/**
 * Builds a complete, standalone HTML page for a thermal receipt.
 * The output is meant to be injected into an <iframe srcdoc=...> for printing
 * via iframe.contentWindow.print() — works on Fully Kiosk, Android Chrome,
 * iPad Safari, and desktop browsers without popups or downloads.
 *
 * All CSS is inline; no Tailwind, no external fonts (uses generic monospace
 * fallback, which renders correctly on the Android print framework).
 */
import QRCode from 'qrcode';

const escapeHtml = (s) => {
    if (s === null || s === undefined) return '';
    return String(s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
};

const fmtMoney = (n) =>
    new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
        minimumFractionDigits: 2,
    }).format(Number(n) || 0);

const fmtDate = (iso) => {
    if (!iso) return '';
    try {
        return new Date(iso).toLocaleString('es-MX', {
            timeZone: 'America/Mexico_City',
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
        });
    } catch { return iso; }
};

const PAYMENT_LABELS = {
    cash: 'Efectivo',
    credit_card: 'Tarjeta de Crédito',
    credit: 'Tarjeta de Crédito',
    debit_card: 'Tarjeta de Débito',
    debit: 'Tarjeta de Débito',
    transfer: 'Transferencia',
    card: 'Tarjeta',
    courtesy: 'Cortesía',
    mixed: 'Pago Mixto',
};

const PAYMENT_LABELS_SHORT = {
    cash: 'Efectivo',
    credit_card: 'TC',
    debit_card: 'TD',
    courtesy: 'Cortesía',
};

/**
 * Build the full HTML document for printing.
 * @param {object} args
 * @param {object} args.sale     — sale object with items[]
 * @param {object} args.company  — company config { legal_name, rfc, address, phone, email, website, logo_url }
 * @param {object} args.config   — receipt config (from GET /api/company/receipt-config)
 * @param {string} args.appUrl   — base URL used to build cert/folio QR links
 * @returns {string} full HTML page string
 */
export const buildReceiptHtml = ({ sale, company = {}, config = {}, appUrl = '', qrMap = {}, logoDataUrl = '', tpvMap = {} }) => {
    const c = config;
    const items = sale?.items || [];
    const width = c.paper_width_mm || 58;
    const fontPx = c.font_size || 11;
    // Use pre-rendered Base64 QR data URLs (passed in via qrMap) so the
    // print job is fully self-contained — no external HTTP requests, no
    // chance of RawBT blocking them, no async image-load races.
    const qrFor = (folio) => qrMap[folio] || '';

    const cleanUrl = (u) => (u || '').replace(/^https?:\/\//, '');

    const headerRow = (key, value) => {
        if (!value) return '';
        return `<div class="row"><span class="lbl">${escapeHtml(key)}</span><span class="val">${escapeHtml(value)}</span></div>`;
    };

    const itemRows = items.map((it) => {
        const folio = it.folio || '';
        const name = it.product_name || (it.product && it.product.name) || 'Certificado';
        const price = fmtMoney(it.unit_price);
        const dest = (it.product && it.product.destination) || it.destination || '';
        return `
            <div class="item">
                <div class="item-head">
                    <span class="item-name">${escapeHtml(name)}</span>
                </div>
                ${c.show_destination && dest ? `<div class="muted small">${escapeHtml(dest)}</div>` : ''}
                ${c.show_folio_per_item && folio ? `<div class="row tight"><span class="mono">${escapeHtml(folio)}</span><span class="val">${escapeHtml(price)}</span></div>` : `<div class="row tight"><span></span><span class="val">${escapeHtml(price)}</span></div>`}
                ${c.show_qr_per_item && folio && qrFor(folio) ? `
                    <div class="qr-wrap">
                        <img src="${qrFor(folio)}" alt="QR ${escapeHtml(folio)}" class="qr"/>
                        <div class="qr-cap small muted">${escapeHtml(c.qr_caption || '')}</div>
                    </div>` : ''}
            </div>`;
    }).join('');

    const headerBits = [];
    if (c.show_logo && (logoDataUrl || company.logo_url)) {
        const logoSrc = logoDataUrl || company.logo_url;
        headerBits.push(`<div class="logo-wrap"><img src="${escapeHtml(logoSrc)}" alt="logo" class="logo"/></div>`);
    }
    if (c.show_company_name && company.legal_name) {
        headerBits.push(`<div class="company">${escapeHtml(company.legal_name)}</div>`);
    }
    if (c.show_rfc && company.rfc) {
        headerBits.push(`<div class="small muted">RFC: ${escapeHtml(company.rfc)}</div>`);
    }
    if (c.show_address && company.address) {
        headerBits.push(`<div class="small muted center">${escapeHtml(company.address)}</div>`);
    }
    const contactRow = [];
    if (c.show_phone && company.phone) contactRow.push(escapeHtml(company.phone));
    if (c.show_email && company.email) contactRow.push(escapeHtml(company.email));
    if (contactRow.length) headerBits.push(`<div class="small muted center">${contactRow.join(' · ')}</div>`);
    if (c.header_message) {
        headerBits.push(`<div class="small center" style="margin-top:4px;">${escapeHtml(c.header_message)}</div>`);
    }

    const customer = sale?.customer || {};
    const seller = sale?.seller || {};
    const metaRows = [];
    if (c.show_date) metaRows.push(headerRow('Fecha:', fmtDate(sale?.created_at)));
    if (c.show_seller && seller.name) metaRows.push(headerRow('Atendió:', seller.name));
    if (c.show_customer_name && customer.name) metaRows.push(headerRow('Cliente:', customer.name));
    if (c.show_customer_phone && customer.phone) metaRows.push(headerRow('Tel:', customer.phone));
    if (c.show_customer_email && customer.email) metaRows.push(headerRow('Correo:', customer.email));

    const totalRows = [];
    if (c.show_total) {
        totalRows.push(`<div class="row total"><span>TOTAL</span><span>${escapeHtml(fmtMoney(sale?.total_amount))}</span></div>`);
    }
    if (c.show_payment_method && sale?.payment_method) {
        const label = PAYMENT_LABELS[sale.payment_method] || sale.payment_method;
        totalRows.push(`<div class="row small muted"><span>Forma de pago:</span><span>${escapeHtml(label)}</span></div>`);
        // Mixed payment: render a line per payment with short method label, TPV name (if any), last4 and amount.
        if (sale.payment_method === 'mixed' && Array.isArray(sale.payments)) {
            sale.payments.forEach((p) => {
                const shortM = PAYMENT_LABELS_SHORT[p.method] || p.method;
                const tpvName = p.tpv_id ? (tpvMap[p.tpv_id] || '') : '';
                const last4 = p.card_last4 ? ` ${p.card_last4}` : '';
                const tpvPart = tpvName ? ` ${tpvName}` : '';
                const desc = `${shortM}${tpvPart}${last4}`;
                totalRows.push(`<div class="row small muted"><span>· ${escapeHtml(desc)}</span><span>${escapeHtml(fmtMoney(p.amount))}</span></div>`);
            });
        } else if (sale.card_last4) {
            // Single card payment: show last4 (helps reconciliation)
            const tpvName = sale.tpv_id ? (tpvMap[sale.tpv_id] || '') : '';
            const parts = [];
            if (tpvName) parts.push(tpvName);
            parts.push(`**** ${sale.card_last4}`);
            totalRows.push(`<div class="row small muted"><span>${escapeHtml(parts.join(' '))}</span><span></span></div>`);
        }
    }

    const footerBits = [];
    if (c.footer_message) footerBits.push(`<div class="center" style="margin-top:6px;font-weight:900;">${escapeHtml(c.footer_message)}</div>`);
    if (c.show_website && company.website) footerBits.push(`<div class="center" style="font-weight:900;">${escapeHtml(cleanUrl(company.website))}</div>`);
    if (c.extra_footer_note) footerBits.push(`<div class="center" style="margin-top:4px;white-space:pre-wrap;font-weight:700;">${escapeHtml(c.extra_footer_note)}</div>`);
    if (c.show_internal_folio && sale?.id) {
        footerBits.push(`<div class="small center" style="margin-top:6px;font-weight:700;">#${escapeHtml(sale.id.slice(0, 8).toUpperCase())}</div>`);
    }

    return `<!doctype html>
<html lang="es">
<head>
<meta charset="utf-8"/>
<title>Recibo ${escapeHtml((sale?.id || '').slice(0, 8))}</title>
<style>
    @page { size: ${width}mm auto; margin: 0; }
    html, body { margin: 0; padding: 0; background: #fff; }
    body {
        font-family: 'Courier New', Courier, monospace;
        color: #000;
        width: ${width}mm;
        padding: 3mm;
        box-sizing: border-box;
        font-size: ${fontPx}px;
        line-height: 1.35;
        font-weight: 700;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
    }
    .center { text-align: center; }
    .muted { color: #000; }
    .small { font-size: ${Math.max(8, fontPx - 2)}px; }
    .mono { font-family: 'Courier New', Courier, monospace; font-weight: 900; }
    .logo-wrap { text-align: center; margin-bottom: 2mm; }
    .logo { max-width: 80%; max-height: 18mm; display: inline-block; }
    .company { font-weight: 900; text-align: center; font-size: ${fontPx + 2}px; margin-bottom: 1mm; }
    .sep { border-top: 1px solid #000; margin: 2mm 0; }
    .row { display: flex; justify-content: space-between; gap: 6px; }
    .row.tight { margin-top: 1mm; }
    .row.total { font-weight: 900; font-size: ${fontPx + 3}px; margin-top: 2mm; }
    .lbl { font-weight: 700; }
    .val { text-align: right; }
    .section-title { font-weight: 900; text-transform: uppercase; letter-spacing: 0.6px; text-align: center; margin: 1.5mm 0; }
    .item { margin-bottom: 3mm; }
    .item-head { font-weight: 900; word-break: break-word; }
    .qr-wrap { text-align: center; margin-top: 1.5mm; }
    .qr { width: 22mm; height: 22mm; image-rendering: pixelated; }
    .qr-cap { margin-top: 0.5mm; }
    @media print {
        body { width: ${width}mm; }
    }
</style>
</head>
<body>
    ${headerBits.join('\n')}
    <div class="sep"></div>
    <div class="section-title">RECIBO DE VENTA</div>
    ${metaRows.join('')}
    <div class="sep"></div>
    <div class="section-title">CERTIFICADOS</div>
    ${itemRows}
    <div class="sep"></div>
    ${totalRows.join('')}
    <div class="sep"></div>
    ${footerBits.join('')}
</body>
</html>`;
};

/**
 * Print a receipt directly from the app — no PDF, no download, no new tab.
 *
 * Strategy: render the receipt inside an off-screen, same-origin <iframe>
 * with its own complete HTML document, then call print() ON THE IFRAME
 * (and fall back to the top window's print on browsers that ignore iframe
 * print calls). Because the iframe is a completely isolated document, the
 * Android Print Framework + RawBT only ever sees the receipt — never the
 * app's UI, the preview iframe of ReceiptConfigPage, or anything else.
 *
 * Works on:
 *   - Fully Kiosk Browser on Android (+ RawBT / Mopria / system printer)
 *   - Standard Android Chrome (+ RawBT / Mopria)
 *   - iPad Safari (AirPrint)
 *   - Desktop Chrome / Firefox / Safari / Edge
 */
export const printReceiptInline = async ({ sale, company, config, appUrl, tpvMap = {} }) => {
    // Pre-generate every QR as a Base64 data URL so the print job has no
    // external HTTP dependencies (RawBT free/paid both render local images
    // much more reliably than externally hosted ones).
    const qrMap = {};
    if (config?.show_qr_per_item) {
        const items = sale?.items || [];
        for (const it of items) {
            if (!it.folio) continue;
            try {
                qrMap[it.folio] = await QRCode.toDataURL(`${appUrl}/cert/${it.folio}`, {
                    errorCorrectionLevel: 'M',
                    margin: 1,
                    scale: 6,
                    color: { dark: '#000000', light: '#FFFFFF' },
                });
            } catch (e) {
                // skip
            }
        }
    }

    // Pre-fetch the company logo and convert to a Base64 data URL for the
    // same reason: thermal print drivers (RawBT specifically) are unreliable
    // at downloading external images mid-print, so we embed the bytes.
    let logoDataUrl = '';
    if (config?.show_logo && company?.logo_url) {
        try {
            const res = await fetch(company.logo_url, { mode: 'cors', credentials: 'omit' });
            if (res.ok) {
                const blob = await res.blob();
                logoDataUrl = await new Promise((res2) => {
                    const fr = new FileReader();
                    fr.onload = () => res2(fr.result);
                    fr.onerror = () => res2('');
                    fr.readAsDataURL(blob);
                });
            }
        } catch (e) {
            // If logo fetch fails (CORS, network), just skip — receipt still prints
        }
    }

    const html = buildReceiptHtml({ sale, company, config, appUrl, qrMap, logoDataUrl, tpvMap });

    return new Promise((resolve) => {
        // Remove any leftover print iframe from a previous call.
        document.querySelectorAll('iframe[data-pos-print-iframe]').forEach((n) => n.remove());

        // Create an off-screen iframe. We use position:fixed + opacity:0 +
        // pointer-events:none (NOT display:none and NOT width:0) so the
        // iframe's document fully lays out, all images load, and the print
        // call can capture the rendered output. Android Print Framework
        // can absolutely print from an off-screen iframe — what it can't
        // do is print from a display:none one.
        const iframe = document.createElement('iframe');
        iframe.setAttribute('data-pos-print-iframe', '1');
        iframe.setAttribute('aria-hidden', 'true');
        const widthMm = config?.paper_width_mm || 58;
        // Width set in CSS pixels (~ 3.78 px/mm) so layout matches print size.
        const widthPx = Math.round(widthMm * 3.78);
        iframe.style.cssText = `
            position: fixed;
            right: 0;
            bottom: 0;
            width: ${widthPx}px;
            height: 1px;
            border: 0;
            padding: 0;
            margin: 0;
            opacity: 0;
            pointer-events: none;
            z-index: -1;
        `;

        document.body.appendChild(iframe);

        const cleanup = () => {
            try { iframe.remove(); } catch (e) { /* noop */ }
            window.removeEventListener('afterprint', cleanup);
        };

        // Restore page after the print dialog closes
        window.addEventListener('afterprint', cleanup);
        // Safety net in case afterprint doesn't fire on Android
        setTimeout(cleanup, 120_000);

        const doPrint = () => {
            try {
                // Try iframe-scoped print first (works on Chrome, Safari,
                // Fully Kiosk Browser, most modern Android WebViews).
                iframe.contentWindow.focus();
                iframe.contentWindow.print();
            } catch (e) {
                // Fallback: some old Android WebViews block iframe print().
                // In that case, fall through to top-window print — but the
                // top window currently still has the app UI, so we'd need
                // a different strategy. For now, just log and try top print.
                try {
                    if (window.fully && typeof window.fully.print === 'function') {
                        window.fully.print();
                    } else {
                        window.print();
                    }
                } catch (e2) { /* noop */ }
            }
            resolve();
        };

        // Write the full receipt HTML into the iframe's document, then wait
        // for the iframe + all its images (QRs, logo) to fully load before
        // firing print().
        const doc = iframe.contentWindow.document;
        doc.open();
        doc.write(html);
        doc.close();

        const waitForImages = () => {
            const imgs = Array.from(doc.images || []);
            if (imgs.length === 0) return Promise.resolve();
            return Promise.all(imgs.map((img) => (
                img.complete && img.naturalWidth > 0
                    ? Promise.resolve()
                    : new Promise((res) => {
                        img.addEventListener('load', res, { once: true });
                        img.addEventListener('error', res, { once: true });
                        setTimeout(res, 2500);
                    })
            )));
        };

        // Some browsers fire load on the iframe immediately after document.close(),
        // others wait. Be safe with both paths.
        const onReady = () => waitForImages().then(() => setTimeout(doPrint, 80));
        if (doc.readyState === 'complete') {
            onReady();
        } else {
            iframe.addEventListener('load', onReady, { once: true });
            // Safety fallback if 'load' never fires
            setTimeout(onReady, 1200);
        }
    });
};
